import { useSettings } from '@capsicum/common';
import { BaseTextInput, SkeletonItem, Text } from '@capsicum/common-web';
import { ChilizChainCircleIcon, CrossSmallIcon } from '@capsicum/common-web/icons';
import { useWCPledgeBasketTexts } from '@capsicum/features/world-championship-pledge/shared';
import { type FormEvent, type KeyboardEvent, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { useFocusBasketItemDigest } from '../hooks/useFocusBasketItemDigest';

type WCPledgeBasketDetailsCardProps = {
	focusedTeamId: null | string;
	onAmountChange: (teamId: string, amount: number) => void;
	onDelete: (teamId: string) => void;
	pledgeAmount: number;
	teamId: string;
	teamImage: string;
	teamName: string;
};

export const WCPledgeBasketDetailsCardSkeleton = () => (
	<S.Card data-testid="wcPledge-basketDetails-amountInputCard">
		<S.CardLeft>
			<SkeletonItem borderRadius="50%" height={24} width={24} />
		</S.CardLeft>
		<div style={{ width: '80%' }}>
			<SkeletonItem borderRadius={4} height={42} />
		</div>
	</S.Card>
);

export const WCPledgeBasketDetailsCard = ({
	focusedTeamId,
	onAmountChange,
	onDelete,
	pledgeAmount,
	teamId,
	teamImage,
	teamName
}: WCPledgeBasketDetailsCardProps) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const { intlTexts } = useWCPledgeBasketTexts();
	const {
		areSettingsLoading,
		settings: { lockerRoomInternationalsConfig }
	} = useSettings();

	useFocusBasketItemDigest(focusedTeamId, teamId, inputRef);

	const preventDecimals = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === '.' || e.key === ',') {
			e.preventDefault();
		}
	}, []);

	const handleAmountChange = useCallback(
		(e: FormEvent<HTMLInputElement>) => {
			const input = e.target as HTMLInputElement;

			const sanitizedValue = input.value.replace(/[^\d]/g, '').replace(/^0+(?=\d)/, '');

			input.value = sanitizedValue;
			onAmountChange(teamId, Number(sanitizedValue));
		},
		[teamId, onAmountChange]
	);

	const handleDelete = useCallback(() => onDelete(teamId), [teamId, onDelete]);

	const error = useMemo(() => {
		return pledgeAmount < lockerRoomInternationalsConfig?.minimumCHZAmountToStake
			? {
					message: intlTexts.minimumPledgeAmount({
						minimumPledgeAmount: lockerRoomInternationalsConfig?.minimumCHZAmountToStake.toString()
					}),
					type: 'error'
				}
			: undefined;
	}, [pledgeAmount, intlTexts]);

	return (
		<S.Card data-testid={`wcPledge-basketDetails-amountInputCard-${teamName}`}>
			<S.CardLeft>
				<S.CardImage alt={`${teamName} flag`} src={teamImage} />
				<Text variant="text1">{teamName}</Text>
			</S.CardLeft>
			<S.CardRight>
				<S.AmountInput
					disabled={areSettingsLoading}
					error={error}
					iconStart={{ icon: ChilizChainCircleIcon }}
					min={lockerRoomInternationalsConfig?.minimumCHZAmountToStake}
					ref={inputRef}
					testID={`wcPledge-basketDetails-amountInputCard-inputField-${teamName}`}
					type="number"
					value={pledgeAmount}
					onInput={handleAmountChange}
					onKeyDown={preventDecimals}
				/>
				<S.BlueCrossIcon
					data-testid={`wcPledge-basketDetails-amountInputCard-deleteButton-${teamName}`}
					style={{ cursor: 'pointer' }}
					onClick={handleDelete}
				/>
			</S.CardRight>
		</S.Card>
	);
};

namespace S {
	export const Card = styled.div`
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		padding: ${({ theme }) => theme.spacings.medium};
		display: flex;
		flex: 1;
		justify-content: space-between;
		align-items: center;
	`;

	export const CardLeft = styled.div`
		display: flex;
		column-gap: ${({ theme }) => theme.spacings.xxsmall};
		align-items: center;
	`;

	export const CardRight = styled.div`
		display: flex;
		column-gap: ${({ theme }) => theme.spacings.small};
		align-items: center;
	`;

	export const CardImage = styled.img`
		width: ${({ theme }) => theme.sizing.small};
		height: ${({ theme }) => theme.sizing.small};
		${({ theme }) => theme.border.radius.circle};
	`;

	export const BlueCrossIcon = styled(CrossSmallIcon)`
		color: ${({ theme }) => theme.colors.icons.brand.enabled};
	`;

	export const AmountInput = styled(BaseTextInput)`
		width: 170px;
		height: 45px;
		display: flex;
		flex-direction: column;
		div {
			height: 100%;
		}

		input {
			text-align: right;
			${({ theme }) => theme.fonts.text.text1};
			font-weight: 700;
		}

		/* Hide the up and down arrows on the input field */
		/* Chrome, Safari, Edge, Opera */
		input::-webkit-outer-spin-button,
		input::-webkit-inner-spin-button {
			-webkit-appearance: none;
			margin: 0;
		}

		/* Firefox */
		input[type='number'] {
			-moz-appearance: textfield;
			appearance: textfield;
		}
	`;
}
